import { b64dec, b64enc, utf8 } from './bytes.js';

const DOMAIN = utf8('timelock-release-v1');

export type OwnerKeypair = {
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  spkiPub: Uint8Array;
  pkcs8Priv: Uint8Array;
};

const ALGO = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const SIGN_ALGO = { name: 'ECDSA', hash: 'SHA-256' } as const;

export async function generateOwnerKeypair(): Promise<OwnerKeypair> {
  const kp = await crypto.subtle.generateKey(ALGO, true, ['sign', 'verify']);
  const spkiPub = new Uint8Array(await crypto.subtle.exportKey('spki', kp.publicKey));
  const pkcs8Priv = new Uint8Array(await crypto.subtle.exportKey('pkcs8', kp.privateKey));
  return { privateKey: kp.privateKey, publicKey: kp.publicKey, spkiPub, pkcs8Priv };
}

export async function importOwnerPrivkey(pkcs8: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('pkcs8', pkcs8 as BufferSource, ALGO, false, ['sign']);
}

export async function signRelease(
  privateKey: CryptoKey,
  lockIdBytes: Uint8Array,
  nonceBytes: Uint8Array,
): Promise<Uint8Array> {
  const msg = new Uint8Array(DOMAIN.length + lockIdBytes.length + nonceBytes.length);
  msg.set(DOMAIN, 0);
  msg.set(lockIdBytes, DOMAIN.length);
  msg.set(nonceBytes, DOMAIN.length + lockIdBytes.length);
  const sig = await crypto.subtle.sign(SIGN_ALGO, privateKey, msg as BufferSource);
  return new Uint8Array(sig);
}

const PEM_BEGIN = '-----BEGIN TIMELOCK KEY-----';
const PEM_END = '-----END TIMELOCK KEY-----';

export function encodeKeyFile(lockIdHex: string, pkcs8Priv: Uint8Array): string {
  const payload = {
    v: 1,
    lock_id: lockIdHex,
    owner_priv: b64enc(pkcs8Priv),
  };
  const b64 = b64enc(utf8(JSON.stringify(payload)));
  const lines = b64.match(/.{1,64}/g) ?? [b64];
  return `${PEM_BEGIN}\n${lines.join('\n')}\n${PEM_END}\n`;
}

export type DecodedKeyFile = { lockIdHex: string; pkcs8Priv: Uint8Array };

export function decodeKeyFile(text: string): DecodedKeyFile | null {
  const start = text.indexOf(PEM_BEGIN);
  const end = text.indexOf(PEM_END);
  if (start < 0 || end < 0 || end <= start) return null;
  const body = text
    .substring(start + PEM_BEGIN.length, end)
    .replace(/\s+/g, '');
  try {
    const raw = b64dec(body);
    const json = new TextDecoder().decode(raw);
    const parsed = JSON.parse(json);
    if (parsed.v !== 1 || typeof parsed.lock_id !== 'string' || typeof parsed.owner_priv !== 'string') {
      return null;
    }
    const pkcs8Priv = b64dec(parsed.owner_priv);
    return { lockIdHex: parsed.lock_id, pkcs8Priv };
  } catch {
    return null;
  }
}
